import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Download } from "lucide-react";
import { RESUME_URL } from "../data/services";

const LINKS = [
  { label: "Work", to: "/services", testid: "nav-services-link" },
  { label: "About", to: "/#about", testid: "nav-about-link" },
  { label: "Projects", to: "/#projects", testid: "nav-projects-link" },
  { label: "AI", to: "/#ai", testid: "nav-ai-link" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Scroll-spy + selection: highlight the nav item for the current section.
  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection("");
      return;
    }
    // Reflect the selected section immediately after a click (via hash).
    if (location.hash) setActiveSection(location.hash.replace("#", ""));

    const ids = ["about", "projects", "ai"];
    const onScrollSpy = () => {
      const marker = window.innerHeight * 0.35;
      let current = "";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= marker && rect.bottom >= marker) current = id;
      }
      if (current) {
        setActiveSection(current);
        return;
      }
      // Clear the highlight when scrolled above the first tracked section
      // (i.e. back near the hero), otherwise keep the last selection.
      const first = document.getElementById(ids[0]);
      if (first && first.getBoundingClientRect().top > marker) {
        setActiveSection("");
      }
    };
    window.addEventListener("scroll", onScrollSpy, { passive: true });
    return () => window.removeEventListener("scroll", onScrollSpy);
  }, [location.pathname, location.hash]);

  // Smooth-scroll to an in-page section on click (reliable with Lenis).
  const handleNavClick = (e, to) => {
    if (!to.includes("#")) return;
    const id = to.split("#")[1];
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) {
        e.preventDefault();
        setActiveSection(id);
        if (window.__lenis) {
          window.__lenis.scrollTo(el, { offset: -80 });
        } else {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  const isLinkActive = (to) => {
    if (to.includes("#")) {
      const id = to.split("#")[1];
      return location.pathname === "/" && activeSection === id;
    }
    return location.pathname === to;
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b ${
        scrolled
          ? "backdrop-blur-xl bg-[#f4f3ee]/85 border-black/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="h-8 w-8 grid place-items-center bg-[#111111] text-white font-mono-tech text-sm group-hover:bg-[#2f4bff] transition-colors duration-300">
            R
          </span>
          <span className="font-head font-bold tracking-tight text-lg leading-none text-[#111111]">
            Rye<span className="text-[#2f4bff]">.</span>Software
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => {
            const active = isLinkActive(l.to);
            return (
              <Link
                key={l.label}
                to={l.to}
                data-testid={l.testid}
                onClick={(e) => handleNavClick(e, l.to)}
                aria-current={active ? "page" : undefined}
                className={`group relative font-mono-tech text-xs uppercase tracking-[0.18em] transition-colors ${
                  active
                    ? "text-[#2f4bff]"
                    : "text-neutral-600 hover:text-[#111111]"
                }`}
              >
                {l.label}
                <span
                  className={`absolute left-0 -bottom-1.5 h-px bg-[#2f4bff] transition-all duration-300 ${
                    active ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            );
          })}
          {/* Résumé download temporarily hidden — restore by uncommenting.
          <a
            href={RESUME_URL}
            download
            data-testid="nav-resume-download"
            className="group inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.15em] text-[#111111] border border-black/20 px-4 py-3 hover:border-[#2f4bff] hover:text-[#2f4bff] transition-colors"
          >
            <Download className="h-4 w-4" />
            Résumé
          </a>
          */}
          <Link
            to="/contact"
            data-testid="nav-contact-button"
            className="group inline-flex items-center gap-2 bg-[#2f4bff] text-white font-mono-tech text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-[#111111] transition-colors duration-300"
          >
            Contact me
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#111111] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#f4f3ee] border-t border-black/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => {
                const active = isLinkActive(l.to);
                return (
                  <Link
                    key={l.label}
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    onClick={(e) => handleNavClick(e, l.to)}
                    aria-current={active ? "page" : undefined}
                    className={`font-head text-2xl font-semibold ${
                      active ? "text-[#2f4bff]" : "text-[#111111]"
                    }`}
                  >
                    {l.label}
                  </Link>
                );
              })}
              {/* Résumé download temporarily hidden — restore by uncommenting.
              <a
                href={RESUME_URL}
                download
                data-testid="mobile-nav-resume"
                className="inline-flex items-center justify-center gap-2 border border-black/20 text-[#111111] font-mono-tech text-sm uppercase tracking-wider px-5 py-4 mt-2"
              >
                <Download className="h-4 w-4" /> Download Résumé
              </a>
              */}
              <Link
                to="/contact"
                data-testid="mobile-nav-contact"
                className="inline-flex items-center justify-center gap-2 bg-[#2f4bff] text-white font-mono-tech text-sm uppercase tracking-wider px-5 py-4"
              >
                Contact me <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
